import { useEffect, useState } from 'react';
import { Alert, Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { functions } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import { trustedStripeCheckoutUrl } from '../lib/redirects';
import { defaultAuthenticatedPath, tenantNeedsBillingAttention } from '../lib/tenantRouting';

export function CheckoutCancelledPage() {
  const navigate = useNavigate();
  const { activeTenant, tenants } = useTenant();
  const ownerTenant = activeTenant?.roles.includes('owner')
    ? activeTenant
    : tenants.find((tenant) => tenant.roles.includes('owner')) || null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckoutSessionForTenant = httpsCallable(functions, 'createCheckoutSessionForTenant');

  useEffect(() => {
    if (ownerTenant && !tenantNeedsBillingAttention(ownerTenant)) {
      navigate(defaultAuthenticatedPath(ownerTenant, ownerTenant.roles), { replace: true });
    }
  }, [navigate, ownerTenant]);

  const handleRetry = async () => {
    if (!ownerTenant) return;

    setLoading(true);
    setError(null);

    try {
      const result = await createCheckoutSessionForTenant({
        tenantId: ownerTenant.tenantId,
        planId: ownerTenant.planId || 'core',
      });
      const data = result.data as { checkoutUrl?: string };
      if (!data.checkoutUrl) {
        throw new Error('Stripe checkout URL was not returned');
      }

      window.location.assign(trustedStripeCheckoutUrl(data.checkoutUrl));
    } catch (retryError: any) {
      console.error('Retry payment error:', retryError);
      setError(retryError.message || 'Unable to reopen Stripe checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 6 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4 }}>
          <Stack spacing={2.5}>
            <Typography variant="h4" fontWeight={700}>
              Checkout was cancelled
            </Typography>
            <Typography color="text.secondary">
              Your workspace is still there. We did not create a duplicate tenant, and you can jump back into the same Stripe Checkout session flow any time.
            </Typography>

            {ownerTenant ? (
              <Alert severity={tenantNeedsBillingAttention(ownerTenant) ? 'warning' : 'info'}>
                Workspace: <strong>{ownerTenant.name}</strong>
                {ownerTenant.planId ? ` • Plan: ${ownerTenant.planId}` : ''}
              </Alert>
            ) : (
              <Alert severity="info">
                We couldn’t find an owner workspace for this account yet. If you just signed up, head back to signup and we’ll resume the pending tenant.
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" disabled={!ownerTenant || loading} onClick={handleRetry}>
                {loading ? 'Opening Stripe...' : 'Retry payment'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/signup')}>
                Back to signup
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default CheckoutCancelledPage;
